import * as React from 'react';
import {
  useResourceContext,
  useDataProvider,
  useGetIdentity,
  useListController,
  FormDataConsumer,
  useRedirect,
  useGetOne,
  SimpleForm,
  useGetList,
  TextInput,
  DateInput,
  required,
  NumberInput,
  SelectInput,
  ReferenceInput,
  useNotify,
  Toolbar,
    Button,
  SaveButton,
} from 'react-admin';
import { Grid } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import moment from 'moment';
import 'moment/locale/fr';
import { useLocation } from 'react-router-dom';

/** =========================
 *  PERSISTENCE LOCK FIELDS
 *  - On stocke { field: { locked: true, value: ... } }
 *  - value n'est JAMAIS undefined
 *  ========================= */
const STORAGE_KEY = 'fish_stocking_locked_fields_v2';

const readLocked = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

const writeLocked = (obj) => localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));

const hasAnyLock = () => Object.keys(readLocked() || {}).length > 0;

const buildDefaultValuesFromLocked = (lockedObj) => {
  const entries = Object.entries(lockedObj || {}).map(([k, v]) => [k, v?.value ?? null]);
  return Object.fromEntries(entries);
};

const LockIcon = ({ field, getValue }) => {
  const [, force] = React.useReducer((x) => x + 1, 0);
  const locked = readLocked();
  const isLocked = !!locked[field]?.locked;

  const toggle = () => {
    const next = readLocked();
    if (next[field]?.locked) {
      delete next[field];
    } else {
      const v = getValue();
      next[field] = { locked: true, value: v ?? null };
    }
    writeLocked(next);
    force();
  };

  return (
      <i
          className={`fa-solid ${isLocked ? 'fa-lock' : 'fa-lock-open'}`}
          onClick={toggle}
          style={{ cursor: 'pointer', marginLeft: 12 }}
          title={
            isLocked
                ? 'Déverrouiller (ne plus persister)'
                : 'Verrouiller (persister pour la prochaine saisie)'
          }
      />
  );
};

const ResetLocks = () => (
    <Button
        label="Réinitialiser les champs persistés"
        startIcon={<RestartAltIcon />}
        onClick={() => {
          localStorage.removeItem(STORAGE_KEY);
          window.location.reload();
        }}
        sx={{
          mb: 2,
          color: 'text.secondary',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
    />
);

/** Toolbar custom (pas de SaveButton RA -> pas d’auto redirect) */
const ManualToolbar = (props) => (
    <Toolbar {...props}>
      <SaveButton type="submit" label="Enregistrer" />
    </Toolbar>
);

/** =========================
 *  COMPONENT
 *  - Submit manuel (aucun redirect automatique possible)
 *  - Si lock actif => rester sur place + reload
 *  ========================= */
const FishStockingCreate = () => {
  const redirect = useRedirect();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const location = useLocation();
  const resource = useResourceContext();
  const { data: identity } = useGetIdentity();

  const { data } = useGetList('campaigns', {
    sort: { field: 'name', order: 'ASC' },
    pagination: { page: 1, perPage: 1000 },
  });

  const choices = React.useMemo(() => {
    if (!data) return [];
    moment.locale('fr');
    return data.map((campaign) => {
      const startDate = moment(campaign.startDate).format('YYYY');
      const endDate = moment(campaign.endDate).format('YYYY');
      return { id: campaign.id, name: `${startDate} / ${endDate}` };
    });
  }, [data]);

  // ⚠️ ton code d'origine lit l'id dans le hash (conservé)
  const queryParameters = new URLSearchParams(window.location.hash.split('?')[1]);
  const id = queryParameters.get('id');

  const waterBody = useGetOne('water_bodies', { id });

  const current = useListController();

  function GetNameSpieces(specieId) {
    // ⚠️ conservé tel quel
    const spieciesName = useGetOne('specie_fishes', { id: specieId });
    if (spieciesName.data) return spieciesName.data.name;
    return spieciesName.data;
  }

  const formatSpecieFish = (record) => {
    if (!record.stage) record.stage = 'Stade non connu';
    return record.stage;
  };

  const searchParams = new URLSearchParams(location.search);
  const isBasin = searchParams.get('isBasin') === 'true';

  // ✅ Default values depuis locks
  const defaultValues = React.useMemo(() => {
    const locked = readLocked();
    const dv = buildDefaultValuesFromLocked(locked);
    // waterBody = toujours celui de l'URL (pas persisté)
    dv.waterBody = waterBody?.data?.id ?? undefined;
    return dv;
  }, [waterBody?.data?.id]);

  /** =========================
   *  SUBMIT MANUEL
   *  ========================= */
  const onSubmit = async (values) => {
    try {
      console.log('test',values);

      if (values.stockingOrigin == "/stocking_origins/1") {
        values.collector = null;
      }
        if (values.stockingOrigin == "/stocking_origins/2") {
            values.waterBodyStockingOrigin = null;
        }
      console.log('test',values.stockingOrigin);
      console.log('test',values.collector);
      console.log('test',values.collector);
      // 0) créer action_fish_stockings
      const createdAction = await dataProvider.create(resource ?? 'action_fish_stockings', {
        data: values,
      });
      const response = createdAction?.data ?? createdAction;

      // 1) créer fish_price si price saisi
      let fishPriceRef = null;

      if (values?.price != null && values.price !== '') {
        const created = await dataProvider.create('fish_prices', {
          data: {
            campaign: response.campaign,
            specieFish: response.specieFish,
            user: `/users/${identity?.id}`,
            costPrice: values.price,
            disposalFeeCollector: values.price,
            purchaseRateCollector: values.price,
          },
        });

        const createdData = created?.data ?? created;
        const iri = createdData?.['@id'];
        const createdId = createdData?.id ?? (iri ? iri.split('/').pop() : null);
        fishPriceRef = iri ?? (createdId != null ? `/fish_prices/${createdId}` : null);
      }
      // 2) update action_fish_stockings avec fishPrice
      if (fishPriceRef) {
        const actionId =
            response?.id ??
            (response?.['@id'] ? parseInt(response['@id'].split('/').pop(), 10) : null);

        if (actionId) {
          await dataProvider.update(resource ?? 'action_fish_stockings', {
            id: actionId,
            data: { ...response, fishPrice: fishPriceRef },
            previousData: response,
          });
        }
      }

      // ✅ Si lock actif => rester sur place
      if (hasAnyLock()) {
        notify('Enregistré. Champs verrouillés conservés pour la prochaine saisie.', {
          type: 'info',
        });
        window.location.reload();
        return;
      }

      // Sinon redirect normal
      const basinParam = isBasin ? '&isBasin=true' : '';
      redirect({
        pathname: '/action_fish_stockings',
        search: `filter=${encodeURIComponent(JSON.stringify({ waterBody: id }))}${basinParam}`,
      });
    } catch (e) {
      console.error(e);
      notify("Erreur lors de l'enregistrement.", { type: 'warning' });

      // si lock => rester sur place
      if (hasAnyLock()) return;

      const basinParam = isBasin ? '&isBasin=true' : '';
      redirect({
        pathname: '/action_fish_stockings',
        search: `filter=${encodeURIComponent(JSON.stringify({ waterBody: id }))}${basinParam}`,
      });
    }
  };

  return (
      <>
        <h1 className={'titleName'}>
          {current !== undefined ? current.defaultTitle : ''} -{' '}
          {waterBody.data !== undefined ? waterBody.data.name : ''}
        </h1>

        <ResetLocks />

        <SimpleForm
            sanitizeEmptyValues
            defaultValues={defaultValues}
            onSubmit={onSubmit}
            toolbar={<ManualToolbar />}
        >
          {/* Campaign */}
          <Grid container alignItems="center">
            <Grid item xs={6}>
              <SelectInput
                  source="campaign"
                  choices={choices}
                  label="Campagne"
                  validate={[required()]}
                  fullWidth
              />
            </Grid>
            <Grid item>
              <FormDataConsumer>
                {({ formData }) => <LockIcon field="campaign" getValue={() => formData.campaign} />}
              </FormDataConsumer>
            </Grid>
          </Grid>

          {/* Affiliation */}
          {isBasin && (
              <Grid container alignItems="center">
                <Grid item xs={6}>
                  <SelectInput
                      source="affiliation"
                      label="Affiliation de l'action"
                      choices={[
                        { id: 'Production estivale 1', name: 'Production estivale 1' },
                        { id: 'Production estivale 2', name: 'Production estivale 2' },
                        { id: 'Stockage hivernal 1', name: 'Stockage hivernal 1' },
                        { id: 'Stockage hivernal 2', name: 'Stockage hivernal 2' },
                      ]}
                      validate={[required()]}
                      fullWidth
                  />
                </Grid>
                <Grid item>
                  <FormDataConsumer>
                    {({ formData }) => (
                        <LockIcon field="affiliation" getValue={() => formData.affiliation} />
                    )}
                  </FormDataConsumer>
                </Grid>
              </Grid>
          )}

          {/* Espèce */}
          <Grid container alignItems="center">
            <Grid item xs={6}>
              <ReferenceInput source="specieFish1" reference="specie_fishes/uniq">
                <SelectInput optionText="name" label="Espèce" validate={[required()]} fullWidth />
              </ReferenceInput>
            </Grid>
            <Grid item>
              <FormDataConsumer>
                {({ formData }) => (
                    <LockIcon field="specieFish1" getValue={() => formData.specieFish1} />
                )}
              </FormDataConsumer>
            </Grid>
          </Grid>

          {/* Stade */}
          <FormDataConsumer>
            {({ formData }) =>
                formData.specieFish1 && (
                    <Grid container alignItems="center">
                      <Grid item xs={6}>
                        <ReferenceInput
                            source="specieFish"
                            sort={{ field: 'id', order: 'DESC' }}
                            reference={'specie_fishes?name=' + GetNameSpieces(formData.specieFish1)}
                        >
                          <SelectInput
                              optionText={formatSpecieFish}
                              label="Saisir stade espèce"
                              validate={[required()]}
                              fullWidth
                          />
                        </ReferenceInput>
                      </Grid>
                      <Grid item>
                        <LockIcon field="specieFish" getValue={() => formData.specieFish} />
                      </Grid>
                    </Grid>
                )
            }
          </FormDataConsumer>

          {/* Nombre têtes */}
          <Grid container alignItems="center">
            <Grid item xs={6} className={'itemForm1'}>
              <NumberInput
                  source="numberHead"
                  label="Nombre de tête(s)"
                  fullWidth
                  onWheel={(e) => e.target.blur()}
              />
            </Grid>
            <Grid item>
              <FormDataConsumer>
                {({ formData }) => (
                    <LockIcon field="numberHead" getValue={() => formData.numberHead} />
                )}
              </FormDataConsumer>
            </Grid>
          </Grid>

          {/* Poids */}
          <Grid container alignItems="center">
            <Grid item xs={3} className={'itemForm1'}>
              <NumberInput
                  source="averageWeight"
                  label="Poids moyen (en kg)"
                  fullWidth
                  onWheel={(e) => e.target.blur()}
              />
            </Grid>
            <Grid item xs={3} className={'itemForm2'}>
              <NumberInput
                  source="totalWeight"
                  label="Poids total (en kg)"
                  validate={[required()]}
                  fullWidth
                  onWheel={(e) => e.target.blur()}
              />
            </Grid>
            <Grid item>
              <FormDataConsumer>
                {({ formData }) => (
                    <>
                      <LockIcon field="averageWeight" getValue={() => formData.averageWeight} />
                      <LockIcon field="totalWeight" getValue={() => formData.totalWeight} />
                    </>
                )}
              </FormDataConsumer>
            </Grid>
          </Grid>

          {/* Origine */}
          <Grid container alignItems="center">
            <Grid item xs={6}>
              <ReferenceInput source="stockingOrigin" reference="stocking_origins" allowEmpty>
                <SelectInput optionText="name" label="Origine" validate={[required()]} fullWidth />
              </ReferenceInput>
            </Grid>
            <Grid item>
              <FormDataConsumer>
                {({ formData }) => (
                    <LockIcon field="stockingOrigin" getValue={() => formData.stockingOrigin} />
                )}
              </FormDataConsumer>
            </Grid>
          </Grid>

          {/* Collecteur */}
          <FormDataConsumer>
            {({ formData }) =>
                formData.stockingOrigin === '/stocking_origins/2' && (
                    <Grid container alignItems="center">
                      <Grid item xs={6}>
                        <ReferenceInput source="collector" reference="collectors">
                          <SelectInput optionText="name" label="Collecteur" fullWidth />
                        </ReferenceInput>
                      </Grid>
                      <Grid item>
                        <LockIcon field="collector" getValue={() => formData.collector} />
                      </Grid>
                    </Grid>
                )
            }
          </FormDataConsumer>

          {/* Etang/bassin origine */}
          <FormDataConsumer>
            {({ formData }) =>
                formData.stockingOrigin === '/stocking_origins/1' && (
                    <Grid container alignItems="center">
                      <Grid item xs={6}>
                        <ReferenceInput source="waterBodyStockingOrigin" reference="water_bodies" perPage={5000}>
                          <SelectInput optionText="name" label="Liste étangs/bassins" fullWidth />
                        </ReferenceInput>
                      </Grid>
                      <Grid item>
                        <LockIcon
                            field="waterBodyStockingOrigin"
                            getValue={() => formData.waterBodyStockingOrigin}
                        />
                      </Grid>
                    </Grid>
                )
            }
          </FormDataConsumer>

          {/* Date */}
          <Grid container alignItems="center">
            <Grid item xs={6} className={'itemForm1'}>
              <DateInput source="date" label="Date" validate={[required()]} fullWidth />
            </Grid>
            <Grid item>
              <FormDataConsumer>
                {({ formData }) => <LockIcon field="date" getValue={() => formData.date} />}
              </FormDataConsumer>
            </Grid>
          </Grid>

          {/* Prix */}
          <FormDataConsumer>
            {({ formData }) =>
                (formData.stockingOrigin === '/stocking_origins/1' ||
                    formData.stockingOrigin === '/stocking_origins/2') && (
                    <Grid container alignItems="center">
                      <Grid item xs={6}>
                        <NumberInput
                            source="price"
                            label="Tarif (en € HT / kg)"
                            fullWidth
                            onWheel={(e) => e.target.blur()}
                        />
                      </Grid>
                      <Grid item>
                        <LockIcon field="price" getValue={() => formData.price} />
                      </Grid>
                    </Grid>
                )
            }
          </FormDataConsumer>

          {/* Note */}
          <Grid container alignItems="center">
            <Grid item xs={6}>
              <TextInput source="note" label="Remarque(s)" multiline fullWidth />
            </Grid>
            <Grid item>
              <FormDataConsumer>
                {({ formData }) => <LockIcon field="note" getValue={() => formData.note} />}
              </FormDataConsumer>
            </Grid>
          </Grid>

          {/* Hidden waterBody */}
          {waterBody.data !== undefined ? (
              <TextInput
                  source="waterBody"
                  className={'hidden'}
                  defaultValue={waterBody.data.id}
                  type={'hidden'}
              />
          ) : (
              ''
          )}
        </SimpleForm>
      </>
  );
};

export default FishStockingCreate;
